import { useMemo } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import {
  buildAdditionalMetricTrends,
  buildBmiAreaData,
  buildGlucoseAreaData,
  buildTshLineData,
  buildVitaminBarData,
  formatChartTickLabel,
  rowsHaveAnyMetrics,
  withChartTickKeys
} from "../chartDataFromReports";
import { BMI_TREND, CHART_COLORS, GLUCOSE_TREND, TSH_TREND, VITAMIN_LEVELS } from "../data";
import ChartShell from "./ChartShell";

const tooltipStyle = {
  contentStyle: {
    borderRadius: 12,
    border: "1px solid rgb(226 232 240)",
    boxShadow: "0 4px 12px rgba(15,23,42,0.08)"
  },
  labelStyle: { fontWeight: 600, color: "#0f172a" }
};

function EmptyChart({ message }) {
  return (
    <div className="flex h-64 items-center justify-center px-4 text-center text-sm text-slate-500 sm:h-72">
      {message}
    </div>
  );
}

/**
 * @param {object} props
 * @param {Array} [props.reports] MediTrack rows (with optional `metrics` from API).
 * @param {boolean} [props.useDemoData=false] When true and live metrics are missing, use bundled demo series.
 */
export default function HealthTrends({ reports = [], useDemoData = false }) {
  const live = rowsHaveAnyMetrics(reports);

  const glucoseData = useMemo(() => {
    const fromReports = buildGlucoseAreaData(reports);
    if (fromReports.length) return fromReports;
    return useDemoData ? withChartTickKeys(GLUCOSE_TREND, "demo-glu") : [];
  }, [reports, useDemoData]);

  const tshData = useMemo(() => {
    const fromReports = buildTshLineData(reports);
    if (fromReports.length) return fromReports;
    return useDemoData ? withChartTickKeys(TSH_TREND, "demo-tsh") : [];
  }, [reports, useDemoData]);

  const vitaminData = useMemo(() => {
    const fromReports = buildVitaminBarData(reports);
    if (fromReports.length) return fromReports;
    return useDemoData ? withChartTickKeys(VITAMIN_LEVELS, "demo-vit") : [];
  }, [reports, useDemoData]);

  const bmiData = useMemo(() => {
    const fromReports = buildBmiAreaData(reports);
    if (fromReports.length) return fromReports;
    return useDemoData ? withChartTickKeys(BMI_TREND, "demo-bmi") : [];
  }, [reports, useDemoData]);

  const additionalTrends = useMemo(() => {
    if (!live) return [];
    return buildAdditionalMetricTrends(reports);
  }, [reports, live]);

  const extraLineColors = useMemo(
    () => [
      CHART_COLORS.indigo,
      CHART_COLORS.success,
      CHART_COLORS.violet,
      CHART_COLORS.warning,
      CHART_COLORS.primary,
      CHART_COLORS.danger,
      CHART_COLORS.secondary
    ],
    []
  );

  const glucoseSubtitle = live
    ? "Fasting or random glucose and HbA1c from your uploaded reports."
    : "Fasting glucose and estimated glycemic trend.";
  const tshSubtitle = live ? "TSH measurements from your thyroid reports." : "Rolling TSH measurements across the year.";
  const vitaminSubtitle = live ? "Vitamin D and B12 from your nutrition-related reports." : "Quarterly nutrition labs.";
  const bmiSubtitle = live ? "BMI values extracted from your uploaded reports." : "Twelve-month body mass index trajectory.";

  return (
    <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2">
      <ChartShell title="Glucose & HbA1c" subtitle={glucoseSubtitle}>
        {glucoseData.length === 0 ? (
          <EmptyChart message="Upload reports that include glucose or HbA1c to see this trend." />
        ) : (
          <div className="h-64 w-full min-h-[14rem] min-w-0 sm:h-72 md:h-80">
            <ResponsiveContainer width="100%" height="100%" minHeight={220}>
              <AreaChart data={glucoseData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="glucoseFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART_COLORS.primary} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={CHART_COLORS.primary} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="hba1cFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART_COLORS.violet} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={CHART_COLORS.violet} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="tickKey"
                  type="category"
                  tickFormatter={(tk) => formatChartTickLabel(glucoseData, tk)}
                  tick={{ fill: "#64748b", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  yAxisId="left"
                  tick={{ fill: "#64748b", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  domain={["auto", "auto"]}
                  label={{ value: "Glucose (mg/dL)", angle: -90, position: "insideLeft", fill: "#64748b", fontSize: 10 }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fill: "#7c3aed", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  domain={["auto", "auto"]}
                  label={{ value: "HbA1c (%)", angle: 90, position: "insideRight", fill: "#7c3aed", fontSize: 10 }}
                />
                <Tooltip {...tooltipStyle} labelFormatter={(label) => formatChartTickLabel(glucoseData, label)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="glucose"
                  name="Glucose (mg/dL)"
                  stroke={CHART_COLORS.primary}
                  fill="url(#glucoseFill)"
                  strokeWidth={2}
                  connectNulls
                />
                <Area
                  yAxisId="right"
                  type="monotone"
                  dataKey="hba1c"
                  name="HbA1c (%)"
                  stroke={CHART_COLORS.violet}
                  fill="url(#hba1cFill)"
                  strokeWidth={2}
                  connectNulls
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </ChartShell>

      <ChartShell title="Thyroid (TSH)" subtitle={tshSubtitle}>
        {tshData.length === 0 ? (
          <EmptyChart message="Upload reports that include TSH to see this trend." />
        ) : (
          <div className="h-64 w-full min-h-[14rem] min-w-0 sm:h-72 md:h-80">
            <ResponsiveContainer width="100%" height="100%" minHeight={220}>
              <LineChart data={tshData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="tickKey"
                  type="category"
                  tickFormatter={(tk) => formatChartTickLabel(tshData, tk)}
                  tick={{ fill: "#64748b", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} domain={["auto", "auto"]} />
                <Tooltip {...tooltipStyle} labelFormatter={(label) => formatChartTickLabel(tshData, label)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  type="monotone"
                  dataKey="tsh"
                  name="TSH (mIU/L)"
                  stroke={CHART_COLORS.indigo}
                  strokeWidth={2.5}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </ChartShell>

      <ChartShell title="Vitamin D & B12" subtitle={vitaminSubtitle}>
        {vitaminData.length === 0 ? (
          <EmptyChart message="Upload reports that include vitamin D or B12 to see this chart." />
        ) : (
          <div className="h-64 w-full min-h-[14rem] min-w-0 sm:h-72 md:h-80">
            <ResponsiveContainer width="100%" height="100%" minHeight={220}>
              <BarChart data={vitaminData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="tickKey"
                  type="category"
                  tickFormatter={(tk) => formatChartTickLabel(vitaminData, tk)}
                  tick={{ fill: "#64748b", fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip {...tooltipStyle} labelFormatter={(label) => formatChartTickLabel(vitaminData, label)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="vitD" name="Vitamin D (ng/mL)" fill={CHART_COLORS.primary} radius={[6, 6, 0, 0]} maxBarSize={40} />
                <Bar dataKey="b12" name="B12 (pg/mL)" fill={CHART_COLORS.success} radius={[6, 6, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </ChartShell>

      <ChartShell title="BMI trend" subtitle={bmiSubtitle}>
        {bmiData.length === 0 ? (
          <EmptyChart message="Upload reports that include BMI (or height/weight if added later) to see this trend." />
        ) : (
          <div className="h-64 w-full min-h-[14rem] min-w-0 sm:h-72 md:h-80">
            <ResponsiveContainer width="100%" height="100%" minHeight={220}>
              <AreaChart data={bmiData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="bmiFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART_COLORS.success} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={CHART_COLORS.success} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="tickKey"
                  type="category"
                  tickFormatter={(tk) => formatChartTickLabel(bmiData, tk)}
                  tick={{ fill: "#64748b", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} domain={["auto", "auto"]} />
                <Tooltip {...tooltipStyle} labelFormatter={(label) => formatChartTickLabel(bmiData, label)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area
                  type="monotone"
                  dataKey="bmi"
                  name="BMI"
                  stroke={CHART_COLORS.success}
                  fill="url(#bmiFill)"
                  strokeWidth={2}
                  connectNulls
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </ChartShell>

      {additionalTrends.length > 0 ? (
        <div className="col-span-1 border-t border-slate-200 pt-6 lg:col-span-2">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Other tests from your reports</h2>
          <p className="mt-1 text-xs text-slate-500">
            Trends for every other numeric result we found in your uploads (CBC, lipids, enzymes, and custom lab lines).
          </p>
        </div>
      ) : null}

      {additionalTrends.map((s, idx) => {
        const stroke = extraLineColors[idx % extraLineColors.length];
        const legendName = s.unit ? `${s.label} (${s.unit})` : s.label;
        return (
          <ChartShell
            key={s.key}
            title={s.label}
            subtitle={
              s.unit
                ? `Values over time (${s.unit}).`
                : "Values over time from your uploaded reports."
            }
          >
            <div className="h-64 w-full min-h-[14rem] min-w-0 sm:h-72 md:h-80">
              <ResponsiveContainer width="100%" height="100%" minHeight={220}>
                <LineChart data={s.data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis
                    dataKey="tickKey"
                    type="category"
                    tickFormatter={(tk) => formatChartTickLabel(s.data, tk)}
                    tick={{ fill: "#64748b", fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} domain={["auto", "auto"]} />
                  <Tooltip {...tooltipStyle} labelFormatter={(label) => formatChartTickLabel(s.data, label)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    name={legendName}
                    stroke={stroke}
                    strokeWidth={2.5}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </ChartShell>
        );
      })}
    </div>
  );
}
